import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { OrdenLiquidacion, OrdenLiquidacionState } from '../types';
import { OrdenLiquidacionService } from '../services/ordenLiquidacionService';

interface OrdenLiquidacionActions {
  fetchOrdenes: () => Promise<void>;
  generarOrden: (idGuiaProduccion: number) => Promise<OrdenLiquidacion | null>;
  updateEstado: (id: number, estado: string) => Promise<boolean>;
  deleteOrden: (id: number) => Promise<boolean>;
  clearError: () => void;
}

type OrdenLiquidacionStore = OrdenLiquidacionState & OrdenLiquidacionActions;

const initialState: OrdenLiquidacionState = {
  ordenes: [],
  loading: false,
  error: null,
};

export const useOrdenLiquidacionStore = create<OrdenLiquidacionStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      fetchOrdenes: async () => {
        set({ loading: true, error: null });
        try {
          const ordenes = await OrdenLiquidacionService.getAll();
          set({ ordenes, loading: false });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Error al cargar órdenes',
            loading: false,
          });
        }
      },

      generarOrden: async (idGuiaProduccion: number) => {
        set({ loading: true, error: null });
        try {
          const orden = await OrdenLiquidacionService.generar(idGuiaProduccion);
          const { fetchOrdenes } = get();
          await fetchOrdenes();
          return orden;
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Error al generar orden',
            loading: false,
          });
          return null;
        }
      },

      updateEstado: async (id: number, estado: string) => {
        set({ loading: true, error: null });
        try {
          await OrdenLiquidacionService.updateEstado(id, estado);
          const { fetchOrdenes } = get();
          await fetchOrdenes();
          return true;
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Error al actualizar estado',
            loading: false,
          });
          return false;
        }
      },

      deleteOrden: async (id: number) => {
        set({ loading: true, error: null });
        try {
          await OrdenLiquidacionService.delete(id);
          const { fetchOrdenes } = get();
          await fetchOrdenes();
          return true;
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Error al eliminar orden',
            loading: false,
          });
          return false;
        }
      },

      clearError: () => set({ error: null }),
    }),
    { name: 'orden-liquidacion-store' }
  )
);
